/**
 * `/login/verify` (spec M0-F3 §8.3) — reproduces
 * `discovery/ui-reference/first-look/login-otp.png` 1:1 with our wordmark and help link:
 * "Check your email" / "Enter the verification code sent to your email" / one `maxlength=6`
 * input / "Verify" disabled until 6 characters / "Didn't get the code?" + "Resend code" /
 * "← Go back to login" / the help footer, in that order.
 *
 * `account_settings.device_verification_enabled` is **false** by default, so this route is
 * unreachable out of the box (ASSUMED, OQ-F3-3).
 */
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTRPCClient } from '../../lib/trpc';
import { AuthError, AuthShell } from './AuthShell';

const RESEND_COOLDOWN_SECONDS = 60;

export function VerifyDevice() {
  const client = useTRPCClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const challengeId = params.get('challenge') ?? '';

  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const verify = useMutation({
    mutationFn: (input: { challengeId: string; code: string; trustDevice: boolean }) =>
      client.auth.verifyDevice.mutate(input),
    onSuccess: () => window.location.assign('/'),
    onError: (caught: unknown) => {
      const shape = caught as { data?: { appCode?: string }; message?: string };
      if (shape.data?.appCode === 'CHALLENGE_BURNED' || shape.data?.appCode === 'CHALLENGE_EXPIRED') {
        void navigate('/login?reason=expired');
        return;
      }
      setError(shape.message ?? "That code isn't right. Try again.");
      setCode('');
      inputRef.current?.focus();
    },
  });

  const resend = useMutation({
    mutationFn: () => client.auth.resendCode.mutate({ challengeId }),
    onSuccess: () => setCooldown(RESEND_COOLDOWN_SECONDS),
    onError: () => setCooldown(RESEND_COOLDOWN_SECONDS),
  });

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (code.length !== 6 || verify.isPending) return;
    setError(null);
    verify.mutate({ challengeId, code, trustDevice: true });
  };

  /** A pasted 6-digit code fills the field and submits itself (§8.3). */
  const onChange = (value: string): void => {
    const digits = value.replace(/\D/g, '').slice(0, 6);
    setCode(digits);
    if (digits.length === 6 && !verify.isPending) {
      setError(null);
      verify.mutate({ challengeId, code: digits, trustDevice: true });
    }
  };

  return (
    <AuthShell
      title="Check your email"
      subtitle="Enter the verification code sent to your email"
      testId="verify-page"
    >
      <form className="ez-auth__form" onSubmit={submit} noValidate>
        <input
          ref={inputRef}
          className="ez-auth__input ez-auth__code"
          type="text"
          maxLength={6}
          inputMode="numeric"
          autoComplete="one-time-code"
          aria-label="Verification code"
          value={code}
          onChange={(event) => onChange(event.target.value)}
          data-testid="verify-code"
        />
        <AuthError>{error}</AuthError>
        <button
          type="submit"
          className="ez-auth__button"
          disabled={code.length !== 6 || verify.isPending}
          data-testid="verify-submit"
        >
          Verify
        </button>
      </form>

      <p className="ez-auth__links" data-testid="verify-resend-row">
        {"Didn't get the code?"}
        <br />
        <button
          type="button"
          className="ez-auth__link"
          disabled={cooldown > 0 || resend.isPending}
          onClick={() => resend.mutate()}
          data-testid="verify-resend"
        >
          {cooldown > 0 ? `Resend code (${cooldown}s)` : 'Resend code'}
        </button>
      </p>

      <p className="ez-auth__links">
        <a className="ez-auth__link" href="/login" data-testid="verify-back">
          ← Go back to login
        </a>
      </p>
    </AuthShell>
  );
}
